import { useNavigate } from 'react-router';
import { logoutUser } from './../../services/authService';
import { appRoutesPaths, baseURL } from './../../settings/appConfig';
import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from 'axios';
import { accessTokenScopes } from '../../settings/authConfig';
import useUI from '../context/useUI';

type HttpMethod = 'get' | 'post' | 'put' | 'patch';

const useHttp = () => {
  const navigate = useNavigate();
  const [getDataResponse, setGetDataResponse] =
    useState<AxiosResponse<any, any>>();
  const [postDataResponse, setPostDataResponse] =
    useState<AxiosResponse<any, any>>();
  const [putDataResponse, setPutDataResponse] =
    useState<AxiosResponse<any, any>>();
  const [patchDataResponse, setPatchDataResponse] =
    useState<AxiosResponse<any, any>>();

  const [refreshAttempted, setRefreshAttempted] = useState(false);
  const { accounts, instance: msalInstance } = useMsal();
  const { showSnackbar } = useUI();

  /** Acquire a fresh access token. */
  const acquireAccessToken = useCallback(async () => {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: accessTokenScopes,
      account: accounts[0],
    });
    return tokenResponse?.accessToken;
  }, [accounts, msalInstance]);

  /** Parse and display validation errors, then throw. */
  const handleValidationErrors = useCallback(
    (error: any) => {
      const messages: string[] = [];
      const errors = error?.response?.data?.errors;

      if (errors && typeof errors === 'object') {
        Object.keys(errors).forEach((key) => {
          const value = errors[key];
          if (Array.isArray(value)) {
            value.forEach((e) => {
              if (typeof e === 'string') messages.push(e);
            });
          }
        });
      }

      if (messages.length > 0) showSnackbar(messages, 'error');
      throw new Error(error);
    },
    [showSnackbar],
  );

  /** Try to refresh token and replay the original axios request. */
  const handleRefreshToken = useCallback(
    async (error: any) => {
      const { config } = error as {
        config?: AxiosRequestConfig & { headers?: Record<string, any> };
      };

      // Only attempt if we have Authorization and not already attempted
      if (config?.headers?.Authorization && !refreshAttempted) {
        try {
          setRefreshAttempted(true);
          const newAccessToken = await acquireAccessToken();

          if (newAccessToken) {
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            return axios.request(config);
          }
          // No token => force logout
          logoutUser(msalInstance);
        } catch (ex) {
          console.log(`Exception while refreshing token: ${ex}`);
          logoutUser(msalInstance);
        }
      }
      // Either no Authorization header or already attempted => propagate error
      throw error;
    },
    [acquireAccessToken, msalInstance, refreshAttempted],
  );

  /** Centralized error handling (reduces code duplication). */
  const handleError = useCallback(
    async (
      error: any,
      setResponse: (resp: AxiosResponse<any, any>) => void,
    ) => {
      if (error?.name === 'InteractionRequiredAuthError') {
        await msalInstance.acquireTokenPopup({ scopes: accessTokenScopes });
        return;
      }

      const status = error?.response?.status;
      switch (status) {
        case 401:
          return handleRefreshToken(error)
            .then((response) => {
              setResponse(response);
              return response;
            })
            .catch((err) => {
              console.log('Failed to refresh access token', err);
            });

        case 400:
          return handleValidationErrors(error);

        case 403:
          navigate(appRoutesPaths.unauthorized);
          return;

        case 404:
          navigate(appRoutesPaths.notFound);
          return;

        default:
          console.log('HTTP request failed', error);
          throw new Error(error);
      }
    },
    [handleRefreshToken, handleValidationErrors, msalInstance, navigate],
  );

  const buildConfig = useCallback(
    (
      accessToken: string,
      cancelToken?: CancelTokenSource,
      needsJson = false,
    ): AxiosRequestConfig => ({
      cancelToken: cancelToken?.token,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...(needsJson ? { 'Content-Type': 'application/json' } : {}),
      },
    }),
    [],
  );

  /**
   * Generic request executor to eliminate method duplication.
   * - Handles token acquisition
   * - Executes axios call
   * - Updates the appropriate response state setter
   * - Routes errors through the centralized handler
   */
  const request = useCallback(
    async <TData = any>(
      method: HttpMethod,
      url: string,
      dataOrCancel?: TData | CancelTokenSource,
      cancelToken?: CancelTokenSource,
      setResponse?: (resp: AxiosResponse<any, any>) => void,
    ) => {
      try {
        const accessToken = await acquireAccessToken();

        // If caller passed cancelToken as the 3rd arg and no explicit 4th arg, normalize
        const hasExplicitCancel = !!cancelToken;
        let maybeCancel: CancelTokenSource | undefined;

        if (hasExplicitCancel) {
          maybeCancel = cancelToken;
        } else if (dataOrCancel instanceof Object && 'token' in dataOrCancel) {
          maybeCancel = dataOrCancel;
        } else {
          maybeCancel = undefined;
        }

        const needsJson = method !== 'get';
        const config = buildConfig(accessToken, maybeCancel, needsJson);

        let response: AxiosResponse<any, any>;
        const fullUrl = baseURL + url;

        switch (method) {
          case 'get':
            response = await axios.get(fullUrl, config);
            break;
          case 'post':
            response = await axios.post(fullUrl, dataOrCancel, config);
            break;
          case 'put':
            response = await axios.put(fullUrl, dataOrCancel ?? {}, config);
            break;
          case 'patch':
            response = await axios.patch(fullUrl, dataOrCancel, config);
            break;
          default:
            throw new Error(`Unsupported method: ${method}`);
        }

        setResponse?.(response);
        return response;
      } catch (error: any) {
        // Delegate to centralized error handling, preserving state setter
        return handleError(error, setResponse!);
      }
    },
    [acquireAccessToken, buildConfig, handleError],
  );

  // Public hook API
  const getData = useCallback(
    async (url: string, cancelToken?: CancelTokenSource) => {
      setGetDataResponse(undefined);
      return request('get', url, undefined, cancelToken, setGetDataResponse);
    },
    [request],
  );

  const postData = useCallback(
    async (url: string, data: any, cancelToken?: CancelTokenSource) => {
      setPostDataResponse(undefined);
      return request('post', url, data, cancelToken, setPostDataResponse);
    },
    [request],
  );

  const putData = useCallback(
    async (url: string, data: any = {}, cancelToken?: CancelTokenSource) => {
      setPutDataResponse(undefined);
      return request('put', url, data, cancelToken, setPutDataResponse);
    },
    [request],
  );

  const patchData = useCallback(
    async (url: string, data: any, cancelToken?: CancelTokenSource) => {
      setPatchDataResponse(undefined);
      return request('patch', url, data, cancelToken, setPatchDataResponse);
    },
    [request],
  );

  return {
    getData,
    getDataResponse,
    postData,
    postDataResponse,
    putData,
    putDataResponse,
    patchData,
    patchDataResponse,
  };
};

export default useHttp;
